import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface CourseStatusChartProps {
  completedCourses: number;
  inProgressCourses: number;
  notStartedCourses: number;
  notCompletedCourses: number; // Added new property
}

/**
 * Component for displaying a pie chart of course status distribution
 */
const CourseStatusChart: React.FC<CourseStatusChartProps> = ({
  completedCourses,
  inProgressCourses,
  notStartedCourses,
  notCompletedCourses // Added new parameter
}) => {
  // Get CSS variables for consistent colors
  const getComputedStyle = window.getComputedStyle(document.documentElement);
  const primaryColor = getComputedStyle.getPropertyValue('--color-primary-main').trim() || '#098ac8'; // Accent color
  const primaryLightColor = getComputedStyle.getPropertyValue('--color-primary-light').trim() || '#3aa5d8';
  const errorColor = getComputedStyle.getPropertyValue('--color-error').trim() || '#f44336';
  const warningColor = getComputedStyle.getPropertyValue('--color-warning').trim() || '#ff9800'; // Warning color for not completed
  
  // Prepare data for the pie chart
  const data = [
    { name: 'Completed', value: completedCourses, color: primaryColor },
    { name: 'In Progress', value: inProgressCourses, color: primaryLightColor },
    { name: 'Not Started', value: notStartedCourses, color: errorColor },
    { name: 'Not Completed', value: notCompletedCourses, color: warningColor } // Added new status
  ].filter(item => item.value > 0); // Only include non-zero values

  // If there's no data, show a message
  if (data.length === 0 || (completedCourses === 0 && inProgressCourses === 0 && notStartedCourses === 0 && notCompletedCourses === 0)) {
    return <div>No course data available for chart</div>;
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
            label={false} // Remove labels from pie segments to avoid overlap
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value) => [`${value} courses`, 'Count']}
          />
          <Legend 
            payload={
              data.map(item => ({
                value: item.name,
                type: 'circle',
                id: item.name,
                color: item.color
              }))
            }
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ 
              paddingTop: '10px',
              position: 'relative',
              marginTop: '-30px'
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CourseStatusChart;
